import { useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

const SOUND_CACHE_NAME = "qqoq-notification-sound-v3";
const SOUND_URL = "/notification-sound.wav";

async function ensureSoundCached(): Promise<boolean> {
  try {
    if (!("caches" in window)) return false;
    const cache = await caches.open(SOUND_CACHE_NAME);
    const cached = await cache.match(SOUND_URL);
    if (cached) {
      console.log("🔔 Notification sound: already cached on device");
      return true;
    }
    console.log("🔔 Notification sound: downloading to device...");
    const response = await fetch(SOUND_URL);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    await cache.put(SOUND_URL, response.clone());
    console.log("🔔 Notification sound: cached successfully");
    const cacheNames = await caches.keys();
    for (const name of cacheNames) {
      if (name.startsWith("qqoq-notification-sound-") && name !== SOUND_CACHE_NAME) {
        await caches.delete(name);
      }
    }
    return true;
  } catch (err) {
    console.warn("🔔 Notification sound cache error:", err);
    return false;
  }
}

export function useNotificationSound() {
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Cache sound on device + preload for instant playback
    ensureSoundCached();
    const audio = new Audio(SOUND_URL);
    audio.preload = "auto";
    audio.volume = 0.7;
    audioRef.current = audio;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "PUSH_NOTIFICATION") {
        const { title, body } = event.data.payload || {};
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        }
        toast({
          title: title || "QQOQ",
          description: body || "",
          duration: 6000,
        });
      }
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleMessage);
    }
    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
      }
    };
  }, [toast]);
}
